import { FormEvent, useEffect, useRef, useState } from 'react';
import { Send, Sparkles, X } from 'lucide-react';

interface Message {
  from: 'bot' | 'user';
  text: string;
}

const QUICK: { key: string; label: string; answer: string }[] = [
  {
    key: 'exam',
    label: 'Quand est mon examen ?',
    answer:
      'Votre date d’examen vous est communiquée par votre auto-école : elle apparaîtra dans votre espace dès qu’elle sera programmée.',
  },
  {
    key: 'pay',
    label: 'Combien me reste-t-il à payer ?',
    answer:
      'Retrouvez le détail de vos règlements dans l’onglet « Mes paiements ».',
  },
  {
    key: 'cancel',
    label: 'Comment annuler une leçon ?',
    answer:
      'Depuis « Réserver une leçon » ou votre tableau de bord, cliquez sur « Annuler » à côté de la leçon : le créneau redevient aussitôt disponible.',
  },
];

const FALLBACK =
  'Pour une question précise sur votre dossier, contactez votre auto-école — elle a accès à l’ensemble de votre formation.';

export default function AssistantWidget({ firstName }: { firstName?: string }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const msgsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages([
      {
        from: 'bot',
        text: `Bonjour ${firstName ?? ''} 👋 Je suis votre assistant Permio. Choisissez un sujet ci-dessous ou posez votre question.`,
      },
    ]);
  }, [firstName]);

  useEffect(() => {
    msgsRef.current?.scrollTo({ top: msgsRef.current.scrollHeight });
  }, [messages, open]);

  const ask = (question: string, answer: string) => {
    setMessages((m) => [
      ...m,
      { from: 'user', text: question },
      { from: 'bot', text: answer },
    ]);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (!q) return;
    setInput('');
    ask(q, FALLBACK);
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-3rem)] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-fade-in">
          <div className="bg-gradient-to-r from-purple-500 via-purple-600 to-indigo-600 text-white px-4 py-3 flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-1.5 flex-shrink-0">
              <Sparkles size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Assistant Permio</div>
              <div className="text-[11px] text-purple-100">Aide rapide</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded-lg hover:bg-white/15 transition-colors"
              aria-label="Fermer"
            >
              <X size={16} />
            </button>
          </div>

          <div ref={msgsRef} className="p-4 space-y-3 max-h-80 overflow-y-auto">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.from === 'user' ? 'justify-end' : ''}`}
              >
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm max-w-[85%] ${
                    m.from === 'user'
                      ? 'bg-indigo-600 text-white rounded-tr-sm'
                      : 'bg-slate-100 text-slate-700 rounded-tl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {QUICK.map((q) => (
              <button
                key={q.key}
                onClick={() => ask(q.label, q.answer)}
                className="px-3 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-medium hover:bg-indigo-100 transition-colors"
              >
                {q.label}
              </button>
            ))}
          </div>

          <form
            onSubmit={onSubmit}
            className="p-3 border-t border-slate-100 flex items-center gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="text"
              placeholder="Poser une question…"
              autoComplete="off"
              className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
            <button
              type="submit"
              className="p-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm transition-colors flex-shrink-0"
              aria-label="Envoyer"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}

      <button
        onClick={() => setOpen((v) => !v)}
        className="group fixed safe-bottom right-6 z-50 overflow-hidden rounded-2xl transition-all duration-300 hover:scale-105 active:scale-95 shadow-2xl"
        aria-label="Assistant Permio"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600" />
        <div className="relative px-5 py-4 flex items-center gap-3">
          <div className="relative">
            <Sparkles
              size={24}
              strokeWidth={2.5}
              className="text-white drop-shadow-lg group-hover:rotate-12 transition-transform duration-300"
            />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-400 rounded-full" />
          </div>
          <div className="text-left">
            <span className="block font-bold text-white tracking-tight text-base">
              Permio
            </span>
            <p className="text-xs text-purple-100 font-medium">
              Votre assistant
            </p>
          </div>
        </div>
      </button>
    </>
  );
}
